import { useState, useEffect, useCallback } from 'react';
import type { Plugin, PluginMetadata } from './plugin-types';
import type { WidgetDefinition } from '@dtwin/shared';

// 插件缓存
const pluginCache = new Map<string, React.ComponentType<any>>();
// 插件模块缓存（原始模块对象，支持 default + widgets 多导出）
const pluginModuleCache = new Map<string, unknown>();

/** 远程插件 URL 白名单（Phase 7 治理）：默认仅允许同源与 /uploads/ 目录；第三方需显式配置 */
export function isRemoteAllowed(url: string): boolean {
  if (!url) return true;
  try {
    if (url.startsWith('/') && !url.startsWith('//')) return true; // 同源相对路径
    const u = new URL(url, window.location.origin);
    const sameOrigin = u.origin === window.location.origin;
    return sameOrigin || u.pathname.startsWith('/uploads/');
  } catch {
    return false;
  }
}

/** 加载插件模块原始对象 */
export const loadPluginModuleRaw = async (url: string): Promise<Record<string, unknown>> => {
  if (!isRemoteAllowed(url)) {
    throw new Error(`插件加载被安全策略拦截：外部 URL 需配置白名单（${url}）`);
  }
  if (pluginModuleCache.has(url)) {
    return pluginModuleCache.get(url) as Record<string, unknown>;
  }
  // 动态导入插件模块（Vite 允许运行时加载）
  const module = await import(/* @vite-ignore */ url);
  pluginModuleCache.set(url, module);
  return module as Record<string, unknown>;
};

/** 从插件模块提取组件定义（形态 B：module.widgets / module.default.widgets） */
export function extractWidgets(module: unknown): WidgetDefinition[] {
  const m = (module || {}) as any;
  const defs = m?.widgets ?? m?.default?.widgets;
  if (Array.isArray(defs)) return defs as WidgetDefinition[];
  return [];
}

/** 从插件模块提取单组件（形态 A：module.default 为 React 组件） */
export function extractComponent(module: unknown): React.ComponentType<any> | null {
  const m = (module || {}) as any;
  const Comp = m?.default || m?.component;
  if (typeof Comp === 'function' || typeof Comp === 'object') return Comp;
  return null;
}

/** 加载插件并同时取组件与组件定义注册表 */
export async function loadPluginWidgets(url: string): Promise<{ component: React.ComponentType<any> | null; widgets: WidgetDefinition[] }> {
  const module = await loadPluginModuleRaw(url);
  return { component: extractComponent(module), widgets: extractWidgets(module) };
}

// 动态加载插件JS
export const loadPluginModule = async (url: string): Promise<React.ComponentType<any>> => {
  if (pluginCache.has(url)) {
    return pluginCache.get(url)!;
  }

  try {
    const module = await loadPluginModuleRaw(url);
    const PluginComponent = extractComponent(module);

    if (!PluginComponent) {
      throw new Error(`插件模块未导出有效的React组件: ${url}`);
    }

    pluginCache.set(url, PluginComponent);
    return PluginComponent;
  } catch (error) {
    console.error(`加载插件失败: ${url}`, error);
    throw error;
  }
};

// 加载插件的Hook
export const usePluginLoader = (plugin: PluginMetadata) => {
  const [PluginComponent, setPluginComponent] = useState<React.ComponentType<any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadPlugin = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const component = await loadPluginModule(plugin.url);
      setPluginComponent(() => component);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [plugin.url]);

  useEffect(() => {
    loadPlugin();
  }, [loadPlugin]);

  const reload = useCallback(() => {
    pluginCache.delete(plugin.url);
    loadPlugin();
  }, [plugin.url, loadPlugin]);

  return { PluginComponent, loading, error, reload };
};

// 预加载插件
export const preloadPlugin = async (plugin: PluginMetadata): Promise<void> => {
  try {
    await loadPluginModule(plugin.url);
  } catch (error) {
    console.warn(`预加载插件失败: ${plugin.id}`, error);
  }
};

// 批量预加载插件
export const preloadPlugins = async (plugins: PluginMetadata[]): Promise<void> => {
  await Promise.allSettled(plugins.map(preloadPlugin));
};